<template>
  <div :id="chartData._id" class="jzliu-bi-engine-chart-warp" :style="{ height: warpHeight }"></div>
</template>

<script>
import { Line } from '@antv/g2plot'
import { mateMapping } from '../../utils'
import { getCategoryDimension, initHeight } from '../../utils/index'
import { customKey, lineTypeMap, lineStyleMap, emptyValueMap } from '../../utils/enum.data'
import { hexToRgba } from '../../utils/index'
import { axisOffset } from '../../utils/panelConf'
import { defaultLineChartConfig } from '../../utils/panelConf'
import { getSeriesFieldValues } from '../../utils/utils'

export default {
  name: 'cLine',
  props: {
    chartData: {
      type: Object,
      default: () => {
        return {}
      },
    },
    chartConfigHandler: {
      type: Function,
      default: (conf) => conf,
    },
  },
  data() {
    return {
      warpHeight: 200,
      lineTypeMap,
      lineStyleMap,
      customKey,
      linePlot: null,
      renderTimer: null,
    }
  },
  watch: {
    'chartData.styleConfig.helperData.fieldMap': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.labelConfig': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.legendConfig': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig.lineChartConfig': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      initHeight.call(this, 'warpHeight')
      this.renderChart()
    })
  },
  methods: {
    // 防抖渲染图表
    debounceRenderChart() {
      if (this.renderTimer) {
        clearTimeout(this.renderTimer)
      }
      this.renderTimer = setTimeout(() => {
        this.linePlot?.destroy()
        this.renderChart()
      }, 300) // 300ms 防抖延迟
    },
    renderChart() {
      const {
        _id: id,
        data: {
          data: chartData,
          meta,
          fields,
        },
      } = this.chartData
      if(!this.chartData.styleConfig.commongConfig?.lineChartConfig){
        this.chartData.styleConfig.commongConfig.lineChartConfig = defaultLineChartConfig()
      }
      const { rows, values, columns } = getCategoryDimension(this.chartData.fields)
      const isSkipValueFlag = this.chartData.styleConfig.commongConfig?.lineChartConfig?.emptyValue === emptyValueMap.skip
      const mateMap = mateMapping(meta)
      const labelConfig = this.chartData.styleConfig.commongConfig.labelConfig ?? {}
      const legendConfig = this.chartData.styleConfig.commongConfig.legendConfig ?? {}
      const conf = {
        xField: 'xField',
        yField: 'yField',
        connectNulls: isSkipValueFlag,
        xAxis:{
          title: {
            text: rows.map(v => this.chartData.styleConfig.helperData.nameMap[v]).join('-') || false,
            style: {
              fill: '#86909C', // 单位文字颜色
              fontSize: 12, // 单位文字字体大小
              lineHeight: 20, // 行高+8 (12 + 8 = 20)
            }
          },
          label: {
            style: {
              fill: '#86909C', // 刻度字体颜色
              fontSize: 12, // 刻度字体大小
              lineHeight: 20, // 行高+8，x轴y轴行高统一
            }
          },
          line: {
            style: {
              stroke: '#EDEFF2', // 轴、刻度颜色
              lineWidth: 1, // 1px
            }
          },
          tickLine: {
            style: {
              stroke: '#EDEFF2', // 刻度线颜色
              lineWidth: 1, // 1px
            }
          }
        },
        yAxis: {
          title: false, // 不显示 Y 轴标题
          label: {
            style: {
              fill: '#86909C', // 刻度字体颜色
              fontSize: 12, // 刻度字体大小
              lineHeight: 20, // 行高+8，x轴y轴行高统一
            }
          },
          line: false, // 不显示 Y 轴线
          tickLine: false, // 不显示 Y 轴刻度线
          grid: {
            line: {
              style: {
                stroke: '#EDEFF2', // 网格线颜色
                lineWidth: 1, // 1px
                lineDash: [2, 2], // 虚线样式：2px 实线，2px 空白
              }
            }
          }
        },
        seriesField: 'seriesField',
        smooth: this.chartData.styleConfig.commongConfig.lineChartConfig.lineType === lineTypeMap.curve, // 折线图平滑曲线
        legend: legendConfig.show ? {
          position: legendConfig.position,
          marker: (text, index, item) => {
            return {
              symbol: 'circle', // 使用圆形标记
              style: {
                fill: this.chartData.styleConfig.helperData.fieldMap[item.name]?.[customKey + 'seriesColor'],
              },
            };
          },
          itemName: {
            style: {
              fill: '#4E5969', // 图例文字颜色
              fontSize: 12, // 字体大小
              lineHeight: 20, // 行高+8 (12 + 8 = 20)
            }
          }
        } : false,
        label: {
          // G2Plot v2 版本的 label 配置
          autoHide: !labelConfig.showAll,
          position: labelConfig.position === '' ? 'point' : labelConfig.position,
          offset: labelConfig.position === 'bottom' ? -10 : labelConfig.position === 'top' ? 10 : 0, // 调整标签位置，10 表示标签在上方，-10 表示标签在下方
          layout: [
            // 折线图使用通用的 overlap 布局调整器
            ...(labelConfig.showAll ? [] : [{ type: 'hide-overlap' }]),
          ],
          style: {
            fontSize: labelConfig.textConfig.fontSize,
            fill: labelConfig.show ? labelConfig.textConfig.color : 'transparent',
            fontWeight: labelConfig.textConfig.isBold ? 'bold' : 'normal',
            fontStyle: labelConfig.textConfig.isItalic ? 'italic' : 'normal',
          }
        } ,
        tooltip: {
          shared: true,
          showCrosshairs: true,
        },
        lineStyle: {
          lineDash: this.chartData.styleConfig.commongConfig.lineChartConfig.lineStyle === lineStyleMap.sty1 ? [] : [2, 2],
          lineWidth: this.chartData.styleConfig.commongConfig.lineChartConfig.lineWidth,
        },
        smooth: this.chartData.styleConfig.commongConfig.lineChartConfig.lineType === lineTypeMap.curve,
        color: (d) => {
          const baseColor = this.chartData.styleConfig.helperData.fieldMap[d.seriesField]?.[customKey + 'seriesColor']
          return hexToRgba(baseColor, 1)
        },
        point: this.chartData.styleConfig.commongConfig.lineChartConfig.showPoint ? {
          size: this.chartData.styleConfig.commongConfig.lineChartConfig.pointSize,
          shape: this.chartData.styleConfig.commongConfig.lineChartConfig.pointShape,
        } : false,
      }

      let dataResult = []
      // 判断是否拥有颜色维度
      const hasColorDimensionFlag = !!columns.length
      const isZeroValueFlag = this.chartData.styleConfig.commongConfig.lineChartConfig.emptyValue === emptyValueMap.zero
      chartData.forEach((v) => {
        /***
         * 这里需要考虑两个逻辑
         1、如果是没有选择颜色维度 则需要创建[度量][维度（多个需要拼接）]笛卡尔乘积 以度量作为seriesField
         2、如果选择颜色维度  度量取第一个 并且以颜色维度作为seriesField
         ***/

        // 构建行维度字段组合
        let rowFields = []
        rows.forEach(rowField => {
          if (v[rowField] !== undefined) {
            rowFields.push(v[rowField])
          }
        })
        const xFieldValue = rowFields.join('-')
        let _xField = ''
        let _yField = ''
        if (!hasColorDimensionFlag) {
          // 没有选择颜色维度，为每个度量值创建一条记录
          values.forEach(valueField => {
            if (v[valueField] !== undefined) {
              _xField = isZeroValueFlag ? xFieldValue ?? 0 : xFieldValue
              _yField = isZeroValueFlag ? v[valueField] ?? 0 : v[valueField]
              dataResult.push({
                ...v,
                xField: _xField,
                yField: _yField,
                seriesField: mateMap.get(valueField) || valueField,
              })
            }
          })
        } else {
          // 有颜色维度，使用第一个度量值和颜色维度
          const yFieldValue = v[values[0]]
          const seriesFieldValue = v[columns[0]]
          if (yFieldValue !== undefined && seriesFieldValue !== undefined) {
            _xField = isZeroValueFlag ? xFieldValue ?? 0 : xFieldValue
            _yField = isZeroValueFlag ? yFieldValue ?? 0 : yFieldValue
            dataResult.push({
              ...v,
              xField: _xField,
              yField: _yField,
              seriesField: seriesFieldValue
            })
          }
        }
      })
      if (!dataResult.length) {
        return
      }

      if (!id) {
        return
      }
      const confOpt = this.chartData.isCancel ? conf : this.chartConfigHandler(conf, this.chartData)
      this.linePlot = new Line(id, {
        ...confOpt,
        data: dataResult,
        meta: {
          ...getSeriesFieldValues(this.chartData)
        },
      })
      this.linePlot.render()
      this.linePlot.on('element:click', (res) => {
        console.log(res, 'resres')
        this.$emit('drill-item-click', res.data)
      });
    },
    destroyedChart() {
      this.linePlot?.destroy()
      this.linePlot.off('element:click')
    }
  },
}
</script>

<style scoped lang="less"></style>
